import React from 'react';
import { Phone, Mail, MapPin, Clock, Instagram, MessageCircle } from 'lucide-react';
import { contactInfo } from '../data/mockData';

const Contact = () => {
  return (
    <div className="min-h-screen pt-24 bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <section className="py-16 bg-gradient-to-r from-[#004534] to-[#0C6951] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="solar-grid-bg w-full h-full"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Get In Touch
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Let's discuss your solar energy needs. We're here to help you make the switch to clean energy.
          </p>
        </div>
      </section>

      {/* Contact Information & Map */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Details */}
            <div className="space-y-8">
              <div className="bg-white rounded-3xl p-8 shadow-lg">
                <h2 className="text-3xl font-bold text-[#004534] mb-8">
                  Contact Information
                </h2>

                <div className="space-y-6">
                  {/* Address */}
                  <div className="flex items-start space-x-4 group">
                    <div className="bg-gradient-to-br from-[#1e3a8a] to-[#22c55e] p-3 rounded-full flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-[#004534] mb-1">Visit Us</h3>
                      <p className="text-gray-600 leading-relaxed">{contactInfo.address}</p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start space-x-4 group">
                    <div className="bg-gradient-to-br from-[#1e3a8a] to-[#22c55e] p-3 rounded-full flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-[#004534] mb-1">Call Us</h3>
                      <a
                        href={`tel:${contactInfo.phone}`}
                        className="text-[#0C6951] hover:text-[#004534] text-xl font-bold transition-colors"
                      >
                        {contactInfo.phone}
                      </a>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start space-x-4 group">
                    <div className="bg-gradient-to-br from-[#1e3a8a] to-[#22c55e] p-3 rounded-full flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-[#004534] mb-1">Email Us</h3>
                      <a
                        href={`mailto:${contactInfo.email}`}
                        className="text-[#0C6951] hover:text-[#004534] text-lg transition-colors"
                      >
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>

                  {/* Working Hours */}
                  <div className="flex items-start space-x-4 group">
                    <div className="bg-gradient-to-br from-[#1e3a8a] to-[#22c55e] p-3 rounded-full flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-[#004534] mb-1">Working Hours</h3>
                      <p className="text-gray-600">{contactInfo.workingHours}</p>
                      <p className="text-sm text-gray-500 mt-1">Sunday Closed</p>
                    </div>
                  </div>

                  {/* Instagram */}
                  <div className="flex items-start space-x-4 group">
                    <div className="bg-gradient-to-br from-[#1e3a8a] to-[#22c55e] p-3 rounded-full flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <Instagram className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-[#004534] mb-1">Follow Us</h3>
                      <a
                        href={`https://instagram.com/${contactInfo.instagram.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#0C6951] hover:text-[#004534] transition-colors"
                      >
                        {contactInfo.instagram}
                      </a>
                      <p className="text-sm text-gray-600 mt-2 leading-relaxed whitespace-pre-line">
                        {contactInfo.caption}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Action Buttons */}
              <div className="grid grid-cols-2 gap-4">
                <a
                  href={`https://wa.me/91${contactInfo.phone}?text=${encodeURIComponent('Hello! I am interested in your solar solutions.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group"
                >
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                  <h3 className="font-bold text-lg">WhatsApp</h3>
                  <p className="text-sm opacity-90 mt-1">Chat with us</p>
                </a>

                <a
                  href={`tel:${contactInfo.phone}`}
                  className="bg-[#22c55e] hover:bg-[#16a34a] text-white rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group"
                >
                  <Phone className="w-12 h-12 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                  <h3 className="font-bold text-lg">Call Now</h3>
                  <p className="text-sm opacity-90 mt-1">Direct line</p>
                </a>
              </div>
            </div>

            {/* Google Map */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg">
              <div className="h-full min-h-[600px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3838.3837393240115!2d74.49776520931418!3d15.836416184746454!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bbf67b97cf5c863%3A0xb1b957193fc439cc!2sShri%20Durga%20Enterprises!5e0!3m2!1sen!2sin!4v1770344744348!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Shri Durga Enterprises Location"
                  className="w-full h-full"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#004534] mb-4">
              Service Areas
            </h2>
            <p className="text-xl text-gray-600">
              We serve clients across three states
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {contactInfo.serviceAreas.map((area, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <MapPin className="w-12 h-12 mx-auto mb-3 text-[#0C6951]" />
                <h3 className="text-xl font-bold text-[#004534]">{area}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#004534] mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Quick answers to common questions
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: 'Do you offer free site surveys?',
                answer: 'Yes! We provide completely free site surveys and energy audits to design the perfect solar solution for your needs.',
              },
              {
                question: 'What is the warranty on solar panels?',
                answer: 'Our solar panels come with a 25-year performance warranty and 10-year product warranty. Installation workmanship is covered for 5 years.',
              },
              {
                question: 'How long does installation take?',
                answer: 'Residential installations typically take 2-3 days. Commercial and industrial projects vary based on capacity, usually 1-2 weeks.',
              },
              {
                question: 'Do you help with government subsidies?',
                answer: 'Absolutely! We assist with all paperwork, government subsidy applications, and net metering approvals to maximize your savings.',
              },
              {
                question: 'What after-sales support do you provide?',
                answer: 'We offer comprehensive maintenance services, 24/7 customer support, annual maintenance contracts, and system monitoring.',
              },
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <h3 className="text-xl font-bold text-[#004534] mb-3">{faq.question}</h3>
                <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#004534] to-[#0C6951] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="solar-grid-bg w-full h-full"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Go Solar?
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Get in touch today for a free consultation and customized solar solution
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <a
              href={`tel:${contactInfo.phone}`}
              className="inline-flex items-center space-x-2 bg-[#D3FF62] hover:bg-[#c4f052] text-[#004534] px-10 py-5 rounded-full font-bold text-xl transition-all duration-300 hover:scale-105 shadow-2xl"
            >
              <Phone className="w-6 h-6" />
              <span>Call Now</span>
            </a>
            <a
              href={`https://wa.me/91${contactInfo.phone}?text=${encodeURIComponent('Hello! I would like to schedule a consultation.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm border-2 border-white hover:bg-white/20 text-white px-10 py-5 rounded-full font-bold text-xl transition-all duration-300 hover:scale-105"
            >
              <MessageCircle className="w-6 h-6" />
              <span>WhatsApp</span>
            </a>
          </div>
        </div>
      </section>

      <style jsx>{`
        .solar-grid-bg {
          background-image: 
            linear-gradient(90deg, rgba(255, 255, 255, 0.3) 1px, transparent 1px),
            linear-gradient(rgba(255, 255, 255, 0.3) 1px, transparent 1px);
          background-size: 80px 80px;
        }
      `}</style>
    </div>
  );
};

export default Contact;
